import { useState, type FormEvent, type ReactNode } from 'react';
import { errorText, fieldProblems } from '@/lib/api';
import { useT } from '@/lib/i18n';
import { Button, Dialog, ErrorBox, Field, Input } from './ui';

/** a name in English and in Khmer — for a product of the platform, or a client's own name for it */
export function RenameDialog({
  title,
  nameEn,
  nameKm,
  hint,
  busy,
  error,
  onSave,
  onReset,
  onClose,
}: {
  title: string;
  nameEn: string;
  nameKm: string;
  hint?: ReactNode;
  busy: boolean;
  error: unknown;
  onSave: (names: { nameEn: string; nameKm: string }) => void;
  /** back to the platform's name (a client's own name only) */
  onReset?: () => void;
  onClose: () => void;
}) {
  const t = useT();
  const [en, setEn] = useState(nameEn);
  const [km, setKm] = useState(nameKm);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    onSave({ nameEn: en.trim(), nameKm: km.trim() });
  };
  return (
    <Dialog
      title={title}
      onClose={onClose}
      footer={
        <>
          {onReset && (
            <Button variant="ghost" onClick={onReset} disabled={busy} className="mr-auto">
              {t('Use the platform name')}
            </Button>
          )}
          <Button variant="secondary" onClick={onClose}>
            {t('Cancel')}
          </Button>
          <Button type="submit" form="rename" disabled={busy || !en.trim() || !km.trim()}>
            {t('Save')}
          </Button>
        </>
      }
    >
      <form id="rename" onSubmit={submit} className="space-y-3">
        {hint && <p className="text-xs text-ink-500">{hint}</p>}
        <Field label={t('Name (English)')}>
          <Input value={en} onChange={(e) => setEn(e.target.value)} maxLength={80} autoFocus />
        </Field>
        <Field label={t('Name (Khmer)')}>
          <Input value={km} onChange={(e) => setKm(e.target.value)} maxLength={80} />
        </Field>
        {error ? <ErrorBox text={[errorText(error), ...fieldProblems(error)].join(' — ')} /> : null}
      </form>
    </Dialog>
  );
}
